import { useEffect, useMemo, useState } from 'react';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '../ui/card';
import { FilterPresets } from '../ui/filter-presets';
import { api } from '../../services/api';
import { cleanFilters, type FilterSettings } from '../../utils/filterPresets';
import { useProfile } from '../../contexts/ProfileContext';
import { hasAdminPrivileges } from '../../utils/role';
import { AiIhsgTsDialog } from './AiIhsgTsDialog';
import { evaluateFilterExpression } from '../../utils/columnFilter';
import {
  AI_LEVEL_COLOR, AI_LEVEL_NAMES, AI_SHAPE_NAMES, aiKpColor, aiShapeColor, fmtYmd, type AiMonitor, type AiMonitorDay, type AiMonitorRow,
} from '../../utils/accdistAi';

// AI Monitoring (internal): what the Acc Dist AI showed on each day, and what the next 3 trading days did.
// Data: backend/ai/accdist/monitor.py → GET /api/accdist-ai/monitor. "Hit" = a High at or above the day's close + 5%
// within 3 trading days (the AI's target); a call is "menunggu" until it hits or its 3 days have passed. A stock whose
// High on D0 itself was already 5% above the close of D-1 ("naik di D0") is yellow instead of red or waiting, and stays
// yellow unless it hits afterwards (green).
// Top: the track record per date. Bottom: ticker × date grid (last GRID_DAYS dates, oldest → newest) — the value shown
// that day, coloured by what happened. Laid out like the Acc Dist menu (control bar, tables, filter row, pagination).

interface Row {
  code: string; p: number | null; kp: number | null; tk: number | null; rk: number | null; tl: string | null; liq: boolean;
  close: number; ho: number | null; hit: 0 | 1 | null;
  h: (number | null)[];  // D+0 (High D0 vs close D-1), then D+1..D+3 (High vs close D0)
  d0: boolean;           // D0's High already reached close D-1 + 5%
}

// Numbers may arrive as strings (a monitor file written with numpy 2 float32 → "4.4"); never let one crash the page
const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toRow = (code: string, r: AiMonitorRow): Row => {
  const [p, kp, tk, rk, tl, liq, close, h1, h2, h3, ho, hit, h0, d0] = r;
  const rkN = num(rk), tkN = num(tk), hitN = num(hit);
  return {
    code, p: num(p), kp: num(kp), tk: tkN ?? (rkN != null ? Math.min(4, Math.floor(rkN * 5)) : null), rk: rkN, tl, liq: Boolean(liq),
    close: num(close) ?? 0, h: [num(h0), num(h1), num(h2), num(h3)], ho: num(ho), hit: hitN === 1 ? 1 : hitN === 0 ? 0 : null,
    d0: num(d0) === 1,
  };
};

// hit / naik di D0 (not hit, rose 5% on D0) / gagal / menunggu add up to n; open = not final yet (yellow ones included)
interface Tally { n: number; hit: number; d0: number; miss: number; wait: number; open: number }
const tally = (rows: Row[]): Tally => ({
  n: rows.length,
  hit: rows.filter(r => r.hit === 1).length,
  d0: rows.filter(r => r.hit !== 1 && r.d0).length,
  miss: rows.filter(r => r.hit === 0 && !r.d0).length,
  wait: rows.filter(r => r.hit == null && !r.d0).length,
  open: rows.filter(r => r.hit == null).length,
});

const pct = (x: number) => `${(x * 100).toFixed(0)}%`;
// Share that hit so far; with calls still waiting it is a lower bound ("≥"). The counts are on the hover card.
const TallyCell = ({ t, base }: { t: Tally; base?: number | undefined }) => {
  if (!t.n) return <span className="text-gray-600">–</span>;
  const rate = t.hit / t.n;
  const good = base != null && t.open === 0 ? (rate > base + 0.02 ? 'text-[#00ff00]' : rate < base - 0.02 ? 'text-[#ffa500]' : 'text-gray-200') : 'text-gray-200';
  return <span className={good}>{t.open ? '≥' : ''}{pct(rate)}</span>;
};

// Summary "Tinggi": Peluang naik at or above this whole percent, as the grid shows it (59.6% reads 60% and counts)
const TINGGI_MIN = 60;
const isTinggi = (r: Row) => r.p != null && Math.round(r.p * 100) >= TINGGI_MIN;

// Summary columns: key in summary(), header, what the group is (shown on hover)
type SumKey = 'all' | 'top10' | 'tinggiLiq' | 'tinggiOther' | 'kp70' | 'kons';
const SUM_COLS: [SumKey, string, string][] = [
  ['all', 'Semua saham', 'Semua saham yang dinilai AI hari itu'],
  ['top10', 'Top 10 peluang', '10 saham dengan Peluang naik tertinggi'],
  ['tinggiLiq', 'Tinggi · likuid', `Peluang naik minimal ${TINGGI_MIN}% di saham likuid`],
  ['tinggiOther', 'Tinggi · kurang likuid', `Peluang naik minimal ${TINGGI_MIN}% di saham kurang likuid`],
  ['kp70', 'AI Prob ≥ 70%', 'Saham dengan AI Prob minimal 70%'],
  ['kons', 'Konsisten Akumulasi', 'Summary Konsisten Akumulasi Kuat atau Melemah'],
];

// Hover card of a Summary cell: the group's hit rate against all stocks, and hit / naik di D0 / gagal / menunggu as a bar.
type SumTip = { t: Tally; label: string; desc: string; date: string; base: number | undefined; rect: DOMRect };
const SUM_TIP_W = 270, SUM_TIP_H = 175;
const SummaryCard = ({ tip, up }: { tip: SumTip; up: number }) => {
  const { t, label, desc, date, base, rect } = tip;
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - SUM_TIP_W / 2, window.innerWidth - SUM_TIP_W - 8));
  const top = rect.bottom + 6 + SUM_TIP_H > window.innerHeight ? rect.top - SUM_TIP_H - 6 : rect.bottom + 6;
  const rate = t.n ? t.hit / t.n : 0;
  const diff = base != null && !t.open ? Math.round((rate - base) * 100) : null;
  const w = (k: number) => `${t.n ? (k / t.n) * 100 : 0}%`;
  return (
    <div className="fixed z-50 pointer-events-none rounded-lg border border-white/15 bg-[#11151d]/95 shadow-xl backdrop-blur-sm p-2.5 text-[12px] text-gray-200"
         style={{ left, top, width: SUM_TIP_W }}>
      <div className="font-bold text-white">{label}<span className="font-normal text-gray-400"> · {fmtYmd(date)}</span></div>
      <div className="text-[11px] text-gray-400">{desc}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-[18px] font-bold text-white">{t.open ? '≥' : ''}{pct(rate)}</span>
        <span className="text-gray-400">naik {up.toFixed(0)}% dalam 3 hari</span>
      </div>
      {diff != null ? (
        <div className="text-[11px] text-gray-400">
          Semua saham {pct(base!)} ·{' '}
          <span className={diff > 2 ? 'text-[#00ff00]' : diff < -2 ? 'text-[#ffa500]' : 'text-gray-200'}>{diff > 0 ? '+' : ''}{diff} poin</span>
        </div>
      ) : t.open ? <div className="text-[11px] text-gray-400">Masih ada yang menunggu hasil 3 hari</div> : null}
      <div className="mt-2 flex h-2 rounded-full overflow-hidden bg-white/5">
        <div className="bg-[#00ff00]/80" style={{ width: w(t.hit) }} />
        <div className="bg-[#ffff00]/70" style={{ width: w(t.d0) }} />
        <div className="bg-[#ff3b3b]/70" style={{ width: w(t.miss) }} />
        <div className="bg-gray-500/60" style={{ width: w(t.wait) }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] whitespace-nowrap">
        <span><span className="text-[#00ff00]">●</span> Hit {t.hit}</span>
        <span title="Sudah naik 5% di D0 (dibanding close D-1), belum kena target sesudahnya"><span className="text-[#ffff00]">●</span> Naik D0 {t.d0}</span>
        <span><span className="text-[#ff3b3b]">●</span> Gagal {t.miss}</span>
        <span><span className="text-gray-400">●</span> Tunggu {t.wait}</span>
      </div>
      <div className="mt-0.5 text-[11px] text-gray-500">dari {t.n} saham</div>
      {t.d0 ? <div className="mt-0.5 text-[10px] text-gray-500">Naik D0 = sudah naik {up.toFixed(0)}% di D0 (vs close D-1), belum kena target sesudahnya</div> : null}
    </div>
  );
};

const fmtMove = (v: number | null) => (v == null ? '–' : `${v > 0 ? '+' : ''}${v.toFixed(1).replace('.', ',')}%`);
const moveColor = (v: number | null) => (v == null ? 'text-gray-500' : v > 0 ? 'text-[#00ff00]' : v < 0 ? 'text-[#ff5c5c]' : 'text-gray-200');

// IHSG beside each date (monitor.py → ihsg.py). The "IHSG nyata" cell = D0's close and its move vs D-1; the hover card
// adds D+1..D+3. The prediction is for the next trading day (D+1, close D+1 vs close D0), made the next morning before
// IDX opens from the US session overnight, and is judged on that.
interface Ihsg {
  close: (number | null)[];     // closes of D-1, D0, D+1, D+2, D+3 (null = not there yet)
  days: (number | null)[];      // day moves of D0, D+1, D+2, D+3
  p: number | null;             // P(IHSG closes D+1 above D0); null until the morning after D0
  wait: boolean;                // p not due yet (that night's US session is still to come)
  right: boolean | null;        // the more likely direction was right (null while waiting / no prediction)
}
const ihsgOf = (d: AiMonitorDay): Ihsg | null => {
  if (!d.ihsg || !Array.isArray(d.ihsg.c)) return null;
  const c = [0, 1, 2, 3, 4].map(i => num(d.ihsg!.c[i]));  // D-1, D0, D+1, D+2, D+3
  if (c[1] == null) return null;
  const chg = (a: number | null, b: number | null) => (a != null && b != null && b > 0 ? (a / b - 1) * 100 : null);
  // moves to the one decimal shown (never a coloured "-0,0%"); ✓/✗ uses the exact closes, as the model's outcome does
  const r1 = (v: number | null) => (v == null ? null : Math.round(v * 10) / 10 || 0);
  const p = num(d.ihsg.p);
  return {
    close: c, days: [1, 2, 3, 4].map(k => r1(chg(c[k] ?? null, c[k - 1] ?? null))), p, wait: Boolean(d.ihsg.w),
    right: p == null || c[2] == null ? null : (p > 0.5) === (c[2] > c[1]),
  };
};
const ihsgCall = (p: number) => (p > 0.5 ? { text: 'Naik', prob: p, cls: 'text-[#00ff00]' } : { text: 'Turun', prob: 1 - p, cls: 'text-[#ff5c5c]' });
const dayLabel = (k: number) => (k === 0 ? 'D0' : `D+${k}`);
const fmtIx = (v: number | null | undefined) => (v == null ? '–' : v.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Hover card of the two IHSG cells: the closes and day moves D0..D+3, and the prediction against what happened.
type IxTip = { x: Ihsg; date: string; rect: DOMRect };
const IX_TIP_W = 268, IX_TIP_H = 200;
const IhsgCard = ({ tip }: { tip: IxTip }) => {
  const { x, date, rect } = tip;
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - IX_TIP_W / 2, window.innerWidth - IX_TIP_W - 8));
  const top = rect.bottom + 6 + IX_TIP_H > window.innerHeight ? rect.top - IX_TIP_H - 6 : rect.bottom + 6;
  const call = x.p != null ? ihsgCall(x.p) : null;
  return (
    <div className="fixed z-50 pointer-events-none rounded-lg border border-white/15 bg-[#11151d]/95 shadow-xl backdrop-blur-sm p-2.5 text-[12px] text-gray-200"
         style={{ left, top, width: IX_TIP_W }}>
      <div className="font-bold text-white">IHSG<span className="font-normal text-gray-400"> · {fmtYmd(date)}</span></div>
      <div className="mt-2 grid grid-cols-4 gap-1 text-center">
        {x.days.map((v, i) => (
          <div key={i} className="rounded py-0.5 bg-white/5">
            <div className="text-[10px] text-gray-400">{dayLabel(i)}</div>
            <div className="text-[11px] text-gray-200">{fmtIx(x.close[i + 1])}</div>
            <div className={moveColor(v)}>{fmtMove(v)}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-[1fr_auto] gap-y-0.5">
        <span className="text-gray-400">Close D-1</span>
        <span className="text-gray-200">{fmtIx(x.close[0])}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-white/10 text-[11px] text-gray-400">
        {call ? (
          <>
            Prediksi pagi sebelum bursa buka, dari bursa AS semalam: peluang IHSG naik besok (D+1){' '}
            <span className="text-gray-200">{pct(x.p!)}</span> → <span className={call.cls}>{call.text}</span>
            {x.right == null ? ' · menunggu D+1' : x.right ? <span className="text-[#00ff00]"> · tepat</span> : <span className="text-[#ffa500]"> · meleset</span>}
          </>
        ) : x.wait ? 'Prediksi untuk besok (D+1) muncul ±07:15 WIB, setelah bursa AS tutup' : 'Tidak ada prediksi untuk tanggal ini'}
      </div>
    </div>
  );
};
// best High of D+1..D+3 against the close of D0 (D+0 is against D-1 and not part of the target)
const maxMove = (r: Row) => r.h.slice(1).reduce<number | null>((m, v) => (v == null ? m : m == null ? v : Math.max(m, v)), null);
// Outcome of a grid cell: hit (≥ +5% within 3 days) / naik di D0 / gagal / still waiting. The text colour doubles as
// the filter's colour token: "ijo" = hit, "kuning" = naik di D0, "merah" = gagal, "netral" = menunggu.
const outcomeColor = (r: Row) =>
  r.hit === 1 ? 'text-[#00ff00]' : r.d0 ? 'text-[#ffff00]' : r.hit === 0 ? 'text-[#ff0000]' : 'text-gray-500';
const outcomeCell = (r: Row) =>
  r.hit === 1 ? 'bg-[#00ff00]/20 text-[#00ff00]' : r.d0 ? 'bg-[#ffff00]/15 text-[#ffff00]'
    : r.hit === 0 ? 'bg-[#ff0000]/15 text-[#ff0000]' : 'text-gray-300';

// Hover card of a grid cell: what the AI said that day and what the next 3 trading days did. Placed beside the cell
// (left of it near the right edge), inside the viewport.
type Tip = { r: Row; date: string; rect: DOMRect };
const TIP_W = 248, TIP_H = 236;
const HoverCard = ({ tip, up }: { tip: Tip; up: number }) => {
  const { r, date, rect } = tip;
  // D+1..D+3 only (D+0 is not the target); the numbers are rounded to 0.1, so colours follow hit / d0, not the numbers
  const hitDay = r.hit === 1 ? r.h.findIndex((v, i) => i > 0 && v != null && v >= up - 0.05) : -1;
  const status = r.hit === 1 ? { text: `Hit D+${hitDay}`, cls: 'bg-[#00ff00]/15 text-[#00ff00]' }
    : r.d0 ? { text: r.hit == null ? 'Naik di D0 · menunggu' : 'Naik di D0', cls: 'bg-[#ffff00]/15 text-[#ffff00]' }
    : r.hit === 0 ? { text: 'Gagal', cls: 'bg-[#ff0000]/15 text-[#ff5c5c]' } : { text: 'Menunggu', cls: 'bg-white/10 text-gray-300' };
  const left = rect.right + 8 + TIP_W > window.innerWidth ? rect.left - TIP_W - 8 : rect.right + 8;
  const top = Math.max(8, Math.min(rect.top - 6, window.innerHeight - TIP_H - 8));
  return (
    <div className="fixed z-50 pointer-events-none rounded-lg border border-white/15 bg-[#11151d]/95 shadow-xl backdrop-blur-sm p-2.5 text-[12px] text-gray-200"
         style={{ left, top, width: TIP_W }}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-white">{r.code}<span className="font-normal text-gray-400"> · {fmtYmd(date)}</span></span>
        <span className={`px-1.5 rounded text-[11px] font-semibold whitespace-nowrap ${status.cls}`}>{status.text}</span>
      </div>
      <div className="mt-2 grid grid-cols-[64px_1fr] gap-y-0.5">
        <span className="text-gray-400">Peluang</span>
        <span className={r.tk != null ? AI_LEVEL_COLOR[r.tk] : ''}>
          {r.p == null ? '–' : pct(r.p)}{r.tk != null ? <span className="text-gray-400"> · {AI_LEVEL_NAMES[r.tk]}</span> : null}
        </span>
        <span className="text-gray-400">AI Prob</span>
        <span className={r.kp != null ? aiKpColor(r.kp) : ''}>{r.kp == null ? '–' : pct(r.kp)}</span>
        {r.tl ? <><span className="text-gray-400">Summary</span><span style={{ color: aiShapeColor(r.tl) }}>{AI_SHAPE_NAMES[r.tl] ?? r.tl}</span></> : null}
      </div>
      <div className="mt-2 pt-2 border-t border-white/10">
        <div className="text-[11px] text-gray-400">Harga tertinggi vs close {r.close.toLocaleString('id-ID')} (target +{up.toFixed(0)}%)</div>
        <div className="mt-1 grid grid-cols-4 gap-1 text-center">
          {r.h.map((v, i) => (
            <div key={i} title={i === 0 ? 'D0: harga tertinggi dibanding close D-1' : undefined}
                 className={`rounded py-0.5 ${r.hit === 1 && i === hitDay ? 'bg-[#00ff00]/15' : i === 0 && r.d0 ? 'bg-[#ffff00]/15' : 'bg-white/5'}`}>
              <div className="text-[10px] text-gray-400">D+{i}</div>
              <div className={v == null ? 'text-gray-500' : i === hitDay ? 'text-[#00ff00]' : i === 0 && r.d0 ? 'text-[#ffff00]'
                : v < 0 ? 'text-[#ff5c5c]' : 'text-gray-200'}>{fmtMove(v)}</div>
            </div>
          ))}
        </div>
        <div className="mt-1 text-[10px] text-gray-500">D+0 = harga tertinggi D0 vs close D-1</div>
        {r.ho != null ? <div className="mt-1 text-[11px] text-gray-400">Kalau beli di open D+1: maks <span className="text-gray-200">{fmtMove(r.ho)}</span></div> : null}
      </div>
    </div>
  );
};

type Show = 'p' | 'kp' | 'max';
const SHOWS: Show[] = ['p', 'kp', 'max'];
const LIQS = ['all', 'liq', 'other'] as const;
const PER_PAGE = [25, 50, 100, 200, 500];
// a saved setting goes back into state only when it is one of the options (an old or edited preset keeps the current one)
const oneOf = <T,>(v: unknown, opts: readonly T[]): T | undefined => ((opts as readonly unknown[]).includes(v) ? (v as T) : undefined);
const GRID_DAYS = 15;
const TRACK_ROWS = 5;     // rows of the Summary shown before it scrolls
const TRACK_ROW_H = 24;   // Summary rows (and its header) are a little taller than the grid's

const thCls = 'px-1 py-0 text-center';
const inputCls = 'w-full bg-transparent text-[12px] text-white placeholder:text-gray-500 focus:outline-none';
const pageBtn = 'p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors';

export function StoryAiMonitoring() {
  const { profile } = useProfile();
  const canView = hasAdminPrivileges(profile?.role || '');  // admin / developer only (also enforced by the API)
  const [data, setData] = useState<AiMonitor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<Show>('p');
  const [liqOnly, setLiqOnly] = useState<'all' | 'liq' | 'other'>('all');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: '', dir: -1 });
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [tip, setTip] = useState<Tip | null>(null);
  const [sumTip, setSumTip] = useState<SumTip | null>(null);
  const [ixTip, setIxTip] = useState<IxTip | null>(null);
  const [tsOpen, setTsOpen] = useState(false);

  useEffect(() => {
    if (!canView) return;
    api.getAccDistAiMonitor().then(r => {
      if (r.success && r.data) setData(r.data);
      else setError(r.error || 'Gagal memuat data monitoring');
    });
  }, [canView]);
  useEffect(() => setPage(1), [filters, liqOnly, sort, show, perPage]);

  const up = (data?.up ?? 0.05) * 100;
  const days = useMemo(() => (data?.dates ?? []).map(d => ({
    ...d, rows: Object.entries(d.stocks).map(([c, r]) => toRow(c, r)), ix: ihsgOf(d),
  })), [data]);
  // the IHSG prediction's record over the dates whose D+1 is in
  const ixRecord = useMemo(() => {
    const done = days.filter(d => d.ix?.right != null);
    return { n: done.length, right: done.filter(d => d.ix!.right).length, up: done.filter(d => d.ix!.close[2]! > d.ix!.close[1]!).length };
  }, [days]);
  const gridDays = useMemo(() => days.slice(0, GRID_DAYS).reverse(), [days]);  // oldest → newest
  // ticker → date → row, for every ticker on the grid's dates
  const byCode = useMemo(() => {
    const m = new Map<string, Record<string, Row>>();
    for (const d of gridDays) for (const r of d.rows) {
      if (!m.has(r.code)) m.set(r.code, {});
      m.get(r.code)![d.date] = r;
    }
    return m;
  }, [gridDays]);

  // Presets: a date column is kept by its place from the newest date ("col0" = newest, "col1" = the one before), so a
  // preset from yesterday applies to today's columns; "ticker" stays as it is. Data, Likuiditas and items per page go in
  // the view.
  const presetCurrent = useMemo<FilterSettings>(() => {
    const rel = (key: string) => {
      if (key === 'ticker') return key;
      const i = days.findIndex(d => d.date === key);
      return i < 0 ? null : `col${i}`;
    };
    const f: Record<string, string> = {};
    for (const [k, expr] of Object.entries(cleanFilters(filters))) {
      const r = rel(k);
      if (r) f[r] = expr;
    }
    const key = sort.key ? rel(sort.key) : null;  // '' = the default (newest date, high → low)
    return { filters: f, sort: key ? { key, direction: sort.dir === 1 ? 'asc' : 'desc' } : null, view: { show, liqOnly, perPage } };
  }, [days, filters, sort, show, liqOnly, perPage]);
  // "col3" → that date today (filters only on the grid's dates; a sort may be on an older Summary date)
  const fromRel = (key: string, within = days.length) => {
    if (key === 'ticker') return key;
    const m = /^col(\d+)$/.exec(key);
    const i = m ? Number(m[1]) : -1;
    return i >= 0 && i < within ? days[i]?.date ?? null : null;
  };
  // The data is loaded once and holds every date, so a preset never needs a refetch; the page goes back to 1 by itself
  const applyPreset = (s: FilterSettings) => {
    const f: Record<string, string> = {};
    for (const [k, expr] of Object.entries(cleanFilters(s?.filters))) {
      const date = fromRel(k, GRID_DAYS);
      if (date) f[date] = expr;
    }
    setFilters(f);
    const so = s?.sort;
    const dir = so?.direction === 'asc' ? 1 : so?.direction === 'desc' ? -1 : 0;
    const key = dir && typeof so?.key === 'string' ? fromRel(so.key) : null;
    setSort(key && dir ? { key, dir } : { key: '', dir: -1 });
    const v: Record<string, unknown> = s?.view ?? {};
    const sh = oneOf(v['show'], SHOWS), lq = oneOf(v['liqOnly'], LIQS), pp = oneOf(v['perPage'], PER_PAGE);
    if (sh) setShow(sh);
    if (lq) setLiqOnly(lq);
    if (pp) setPerPage(pp);
  };
  // column key → its header on today's grid
  const presetLabel = (key: string) => {
    if (key === 'ticker') return 'Ticker';
    const date = fromRel(key);
    return date ? fmtYmd(date).replace(/ \d{4}$/, '') : key;
  };

  if (!canView) return <div className="p-6 text-sm text-muted-foreground">Menu ini hanya untuk admin dan developer.</div>;
  if (error) return <div className="p-6 text-sm text-[#ffa500]">{error}</div>;
  if (!data) {
    // loading: the same blue ribbon as the Acc Dist menu
    return (
      <div className="flex flex-col h-[calc(100vh-64px)] relative">
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }} />
        </div>
        <style>{`@keyframes loading { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
      </div>
    );
  }
  if (!days.length) return <div className="p-6 text-sm text-muted-foreground">Belum ada data monitoring.</div>;

  // Track record per date: all, Tinggi (Peluang naik ≥ TINGGI_MIN) per liquidity group, top 10 by peluang, AI Prob ≥ 70%,
  // Konsisten Akumulasi
  const summary = (d: AiMonitorDay & { rows: Row[] }) => {
    const base = d.days_passed >= (data.horizon ?? 3) ? tally(d.rows).hit / Math.max(1, d.rows.length) : undefined;
    // by peluang, ties (the calibration is flat at the top) broken by the score's rank in its group, as the AI orders them
    const top10 = [...d.rows].filter(r => r.p != null).sort((a, b) => (b.p ?? 0) - (a.p ?? 0) || (b.rk ?? 0) - (a.rk ?? 0)).slice(0, 10);
    return {
      base,
      all: tally(d.rows),
      tinggiLiq: tally(d.rows.filter(r => isTinggi(r) && r.liq)),
      tinggiOther: tally(d.rows.filter(r => isTinggi(r) && !r.liq)),
      top10: tally(top10),
      kp70: tally(d.rows.filter(r => (r.kp ?? 0) >= 0.7)),
      kons: tally(d.rows.filter(r => r.tl === 'KAK' || r.tl === 'KAM')),
    };
  };

  const valueOf = (r: Row | undefined): number | null =>
    !r ? null : show === 'p' ? (r.p == null ? null : r.p * 100) : show === 'kp' ? (r.kp == null ? null : r.kp * 100) : maxMove(r);
  const cellText = (r: Row) => {
    const v = valueOf(r);
    return v == null ? '–' : show === 'max' ? fmtMove(v) : `${v.toFixed(0)}%`;
  };

  const latest = gridDays[gridDays.length - 1]!.date;
  const sortKey = sort.key || latest;
  const pass = (code: string) => {
    const rows = byCode.get(code)!;
    if (filters['ticker'] && !evaluateFilterExpression(filters['ticker'], { value: NaN, text: code.toLowerCase(), colorClass: null, isNumeric: false })) return false;
    if (liqOnly !== 'all') {
      const r = rows[latest] ?? Object.values(rows)[0];
      if (!r || (liqOnly === 'liq') !== r.liq) return false;
    }
    for (const d of gridDays) {
      const expr = filters[d.date];
      if (!expr || !expr.trim()) continue;
      const r = rows[d.date];
      if (!r) return false;
      const raw = valueOf(r);
      // the number the cell shows (whole % or one decimal), so ">60" never keeps a cell that reads 60%; a cell without
      // a number (no D+1 yet in "Kenaikan tertinggi") fails every number test but keeps its colour
      const v = raw == null ? NaN : show === 'max' ? Math.round(raw * 10) / 10 : Math.round(raw);
      const colour = outcomeColor(r);
      if (!evaluateFilterExpression(expr, { value: v, text: raw == null ? '' : String(v), colorClass: colour, isNumeric: true })) return false;
    }
    return true;
  };
  const codes = [...byCode.keys()].filter(pass).sort((a, b) => {
    if (sortKey === 'ticker') return (a < b ? -1 : 1) * sort.dir;
    const x = valueOf(byCode.get(a)![sortKey]), y = valueOf(byCode.get(b)![sortKey]);
    if (x == null && y == null) return a < b ? -1 : 1;
    if (x == null) return 1;
    if (y == null) return -1;
    return (x - y) * sort.dir;
  });
  const totalPages = Math.max(1, Math.ceil(codes.length / perPage));
  const start = (page - 1) * perPage;
  const shown = codes.slice(start, start + perPage);
  const toggleSort = (key: string) =>
    setSort(o => ({ key, dir: (o.key || latest) === key ? (o.dir === -1 ? 1 : -1) : (key === 'ticker' ? 1 : -1) }));
  const arrow = (key: string) => (sortKey === key ? <span className="text-[10px]">{sort.dir === 1 ? ' ↑' : ' ↓'}</span> : null);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] gap-2 relative">
      {/* Top Control Bar */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 shrink-0">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 md:gap-x-7 md:gap-y-2">
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Data:</label>
            <select value={show} onChange={e => setShow(e.target.value as Show)}
                    className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto">
              <option value="p">Peluang naik</option>
              <option value="kp">AI Prob</option>
              <option value="max">Kenaikan tertinggi</option>
            </select>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Likuiditas:</label>
            <select value={liqOnly} onChange={e => setLiqOnly(e.target.value as 'all' | 'liq' | 'other')}
                    className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto">
              <option value="all">Semua</option>
              <option value="liq">Likuid</option>
              <option value="other">Kurang likuid</option>
            </select>
          </div>
          {/* right-aligned on a phone as well: the Presets list opens leftwards from the button */}
          <div className="flex justify-end w-full md:w-auto md:ml-auto">
            <FilterPresets page="ai-monitoring" current={presetCurrent} onApply={applyPreset} label={presetLabel} />
          </div>
        </div>
      </div>

      {/* Track record per date (newest first; TRACK_ROWS rows, the rest scrolls) */}
      {/* gap-0: the Card's default gap would leave an empty band between the title and the table */}
      <Card className="p-0 gap-0 bg-black border-gray-800 overflow-hidden rounded-none w-full shrink-0">
        <h3 className="text-[12px] font-bold px-2 py-1 bg-gray-900 text-white border-b border-white/30" title={data.target}>Summary</h3>
        <div className="overflow-auto" style={{ maxHeight: TRACK_ROW_H * (TRACK_ROWS + 1) + 1 }} onScroll={() => { setSumTip(null); setIxTip(null); }}>
          <table className="w-full text-[12px] tabular-nums table-fixed min-w-[1000px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10">
              {/* one line: the box is TRACK_ROW_H per row; a header that does not fit is cut, with its name on hover */}
              <tr className="h-[24px] whitespace-nowrap divide-x divide-white/30">
                <th className={`${thCls} w-[150px]`}>Tanggal</th>
                <th className={`${thCls} w-[70px]`} title="Hari bursa yang sudah lewat dari 3">Hari lewat</th>
                {SUM_COLS.map(([k, label, desc]) => <th key={k} className={`${thCls} truncate`} title={`${label}: ${desc}`}>{label}</th>)}
                <th className={`${thCls} w-[132px]`}
                    title="IHSG close hari itu (D0) dan, dalam kurung, perubahannya dibanding hari bursa sebelumnya · D+1..D+3 saat kursor di atas sel">
                  IHSG nyata
                </th>
                <th className={`${thCls} w-[138px] truncate cursor-pointer underline decoration-dotted underline-offset-2 hover:text-[#38bdf8]`}
                    onClick={() => setTsOpen(true)}
                    title={'Arah IHSG besok (D+1: close D+1 vs close D0) yang lebih mungkin dan peluang arah itu, dibuat pagi sebelum bursa buka (±07:15 WIB) dari bursa AS semalam: S&P 500, Nasdaq, futures, VIX, rupiah, EIDO. Setelah D+1: ✓ tepat / ✗ meleset (gerak D+1 ada saat kursor di atas sel)'
                      + (ixRecord.n ? `\nDi tanggal-tanggal ini: tepat ${ixRecord.right} dari ${ixRecord.n} hari (${pct(ixRecord.right / ixRecord.n)}), IHSG naik di ${ixRecord.up} hari` : '')
                      + '\nKlik: studi time series asing & domestik → IHSG besok'}>
                  IHSG prediksi besok
                </th>
              </tr>
            </thead>
            <tbody>
              {days.map(d => {
                const x = summary(d);
                return (
                  <tr key={d.date} onClick={() => setSort({ key: d.date, dir: -1 })}
                      className={`h-[24px] divide-x divide-white/30 cursor-pointer hover:bg-white/[0.25] ${d.date === sortKey ? 'bg-blue-900/30' : ''}`}>
                    <td className="px-1 py-0 text-left truncate">
                      {fmtYmd(d.date)}{!d.recomputed && !d.method ? <span className="text-gray-500"> · versi lama</span> : null}
                    </td>
                    <td className="px-1 py-0 text-center">{d.days_passed}/{data.horizon}</td>
                    {SUM_COLS.map(([k, label, desc]) => {
                      const base = k === 'all' ? undefined : x.base;
                      return (
                        <td key={k} className="px-1 py-0 text-right truncate"
                            onMouseEnter={e => x[k].n && setSumTip({ t: x[k], label, desc, date: d.date, base, rect: e.currentTarget.getBoundingClientRect() })}
                            onMouseLeave={() => setSumTip(null)}>
                          <TallyCell t={x[k]} base={base} />
                        </td>
                      );
                    })}
                    {(() => {
                      const ix = d.ix;
                      const hover = ix ? {
                        onMouseEnter: (e: React.MouseEvent<HTMLTableCellElement>) => setIxTip({ x: ix, date: d.date, rect: e.currentTarget.getBoundingClientRect() }),
                        onMouseLeave: () => setIxTip(null),
                      } : {};
                      const call = ix?.p != null ? ihsgCall(ix.p) : null;
                      return (
                        <>
                          <td className="px-1 py-0 text-right truncate" {...hover}>
                            {!ix ? <span className="text-gray-600">–</span> : (
                              <>
                                <span className="text-gray-200">{fmtIx(ix.close[1])}</span>
                                {ix.days[0] != null ? <span className={moveColor(ix.days[0])}> ({fmtMove(ix.days[0])})</span> : null}
                              </>
                            )}
                          </td>
                          <td className="px-1 py-0 text-right truncate" {...hover}>
                            {!call ? <span className="text-gray-600">{ix?.wait ? 'besok pagi' : '–'}</span> : (
                              <>
                                <span className={call.cls}>{call.text} {pct(call.prob)}</span>
                                {ix!.right == null ? null : ix!.right ? <span className="text-[#00ff00]"> ✓</span> : <span className="text-[#ffa500]"> ✗</span>}
                              </>
                            )}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {tip ? <HoverCard tip={tip} up={up} /> : null}
      {sumTip ? <SummaryCard tip={sumTip} up={up} /> : null}
      {ixTip ? <IhsgCard tip={ixTip} /> : null}
      <AiIhsgTsDialog open={tsOpen} onOpenChange={setTsOpen} />

      {/* Ticker × date grid */}
      <Card className="flex flex-col flex-1 min-h-0 p-0 gap-0 bg-black border-gray-800 overflow-hidden rounded-none w-full">
        <h3 className="text-[12px] font-bold px-2 py-1 bg-gray-900 text-white border-b border-white/30 shrink-0">Ticker Detail</h3>
        <div className="flex-auto min-h-0 overflow-auto bg-black" onScroll={() => setTip(null)}>
          <table className="w-full text-[12px] tabular-nums table-fixed min-w-[1020px] leading-tight border-separate border-spacing-0 bg-black">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10">
              <tr className="cursor-pointer select-none h-[22px] divide-x divide-white/30">
                <th onClick={() => toggleSort('ticker')} className={`${thCls} w-[60px] sticky left-0 z-20 bg-gray-900`}>Ticker{arrow('ticker')}</th>
                {gridDays.map(d => (
                  <th key={d.date} onClick={() => toggleSort(d.date)} className={`${thCls} w-[64px] ${d.date === latest ? 'bg-emerald-500/20' : ''}`}
                      title={`${fmtYmd(d.date)} · ${d.days_passed}/${data.horizon} hari bursa sudah lewat${d.recomputed ? ' · dihitung ulang' : ''}`}>
                    {fmtYmd(d.date).replace(/ \d{4}$/, '')}{arrow(d.date)}
                  </th>
                ))}
              </tr>
              {/* Filter row: ticker text; per date numbers (">50") or colours (ijo = hit, kuning = naik di D0, merah = gagal, netral = menunggu) */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                <td className="px-1 py-0 sticky left-0 z-20 bg-gray-800">
                  <input className={`${inputCls} text-left`} placeholder="Filter" value={filters['ticker'] || ''}
                         onChange={e => setFilters(f => ({ ...f, ticker: e.target.value }))} />
                </td>
                {gridDays.map(d => (
                  <td key={d.date} className="px-1 py-0">
                    <input className={`${inputCls} text-right`} placeholder={show === 'max' ? '>5' : '>50'}
                           title="Angka: >50, <30 · warna: ijo = hit, kuning = naik di D0, merah = gagal, netral = menunggu"
                           value={filters[d.date] || ''} onChange={e => setFilters(f => ({ ...f, [d.date]: e.target.value }))} />
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map(code => (
                <tr key={code} className="h-[18px] divide-x divide-white/30 hover:bg-white/[0.25]">
                  <td className="px-1 py-0 text-left text-gray-300 sticky left-0 z-[5] bg-black">{code}</td>
                  {gridDays.map(d => {
                    const r = byCode.get(code)![d.date];
                    if (!r) return <td key={d.date} className="px-1 py-0 text-right text-gray-600">–</td>;
                    return (
                      <td key={d.date} className={`px-1 py-0 text-right ${outcomeCell(r)}`}
                          onMouseEnter={e => setTip({ r, date: d.date, rect: e.currentTarget.getBoundingClientRect() })}
                          onMouseLeave={() => setTip(null)}>
                        {cellText(r)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls (as in Acc Dist) */}
        <div className="border-t border-border bg-muted/30 px-2 py-1 flex flex-col sm:flex-row items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Items per page:</span>
            <select value={perPage} onChange={e => setPerPage(Number(e.target.value))}
                    className="px-1 py-0.5 text-[11px] border border-border rounded-md bg-background text-foreground h-6">
              {PER_PAGE.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Showing {codes.length ? start + 1 : 0} - {Math.min(start + perPage, codes.length)} of {codes.length}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1} className={pageBtn} aria-label="First page"><ChevronFirst className="w-3 h-3" /></button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className={pageBtn} aria-label="Previous page"><ChevronLeft className="w-3 h-3" /></button>
            <span className="text-[11px] text-foreground px-1">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className={pageBtn} aria-label="Next page"><ChevronRight className="w-3 h-3" /></button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className={pageBtn} aria-label="Last page"><ChevronLast className="w-3 h-3" /></button>
          </div>
        </div>
      </Card>
    </div>
  );
}
